/**
 * usePeppermintWidget Composable
 * Mounts the Peppermint support widget (public/peppermint-widget.js) for
 * logged-in system admins and project admins, and tears it down again when
 * the user no longer qualifies (logout, role/company change).
 *
 * The widget's own floating action button is hidden; the ticket form is
 * opened from the navbar user dropdown via openPeppermintWidget().
 */

import { watch } from "vue";
import { usePermissions } from "@/composables/usePermissions";
import { useAuthStore } from "@/stores";

const SCRIPT_ID = "peppermint-widget-script";
const ROOT_ID = "peppermint-widget-root";
const STYLE_ID = "peppermint-widget-style";

function hideFloatingButton() {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `#${ROOT_ID} .pm-fab { display: none !important; }`;
  document.head.appendChild(style);
}

/**
 * Opens the ticket form by triggering the widget's (hidden) trigger button.
 * The button only exists while the form is closed, so a second call while the
 * form is already open is a no-op. Retries briefly in case the widget script
 * has not finished loading yet.
 *
 * @returns {Promise<boolean>} true when the form was opened
 */
export function openPeppermintWidget(retries = 10) {
  const fab = document.querySelector(`#${ROOT_ID} .pm-fab`);
  if (fab) {
    fab.click();
    return Promise.resolve(true);
  }

  if (retries <= 0) return Promise.resolve(false);

  return new Promise((resolve) => {
    setTimeout(() => resolve(openPeppermintWidget(retries - 1)), 200);
  });
}

export function usePeppermintWidget() {
  const { isSystemAdmin, isProjectAdmin } = usePermissions();
  const authStore = useAuthStore();

  function mountWidget() {
    if (document.getElementById(SCRIPT_ID)) return;

    const apiKey = process.env.VUE_APP_PEPPERMINT_API_KEY;
    const apiUrl = process.env.VUE_APP_PEPPERMINT_API_URL;
    if (!apiKey || !apiUrl) return;

    const user = authStore.user;

    hideFloatingButton();

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = "/peppermint-widget.js";
    script.dataset.apiKey = apiKey;
    script.dataset.apiUrl = apiUrl;
    script.dataset.appName = "Mind One";
    script.dataset.userName = user ? `${user.first_name} ${user.last_name}`.trim() : "";
    script.dataset.userEmail = user?.username || "";
    document.body.appendChild(script);
  }

  function unmountWidget() {
    document.getElementById(ROOT_ID)?.remove();
    document.getElementById(SCRIPT_ID)?.remove();
    document.getElementById(STYLE_ID)?.remove();
  }

  watch(
    () => isSystemAdmin.value || isProjectAdmin.value,
    (visible) => (visible ? mountWidget() : unmountWidget()),
    { immediate: true },
  );
}
